import React, { useRef } from 'react';
import { Camera, Image, Trash } from 'phosphor-react';
import { SFooterDrawer } from '@/components/ui/organisms/SFooterDrawer';
import { triggerNativeCamera, triggerNativeGallery } from '@/utils/nativeBridge';
import { useNativeMessages } from '@/hooks/useNativeMessages';
import { MediaResult } from '@/types/nativeBridge.types';
import './AvatarEditDrawer.css';

export interface AvatarEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onTakePhoto?: (file: File) => void;
  onChooseFromLibrary?: (file: File) => void;
  onDeletePhoto?: () => void;
}

export const AvatarEditDrawer: React.FC<AvatarEditDrawerProps> = ({
  isOpen,
  onClose,
  onTakePhoto,
  onChooseFromLibrary,
  onDeletePhoto,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Convert base64 to File object
  const base64ToFile = async (base64: string, fileName: string): Promise<File> => {
    const response = await fetch(`data:image/jpeg;base64,${base64}`);
    const blob = await response.blob();
    return new File([blob], fileName, { type: 'image/jpeg' });
  };

  // Handle camera result from native
  const handleNativeCameraResult = async (result: MediaResult) => {
    console.log('[AvatarEditDrawer] Camera result:', result);
    
    if (!result.success) {
      console.error('[AvatarEditDrawer] Camera failed:', result.error);
      if (result.error && result.error !== 'User canceled') {
        alert(`Camera error: ${result.error}`);
      }
      return;
    }

    if (result.base64 && onTakePhoto) {
      try {
        const file = await base64ToFile(result.base64, result.fileName || 'camera-photo.jpg');
        console.log('[AvatarEditDrawer] Camera file created:', file.name, file.size);
        onTakePhoto(file);
      } catch (err) {
        console.error('[AvatarEditDrawer] Error converting camera base64:', err);
        alert('Failed to process camera photo');
      }
    }
  };

  // Handle gallery result from native
  const handleNativeGalleryResult = async (result: MediaResult) => {
    console.log('[AvatarEditDrawer] Gallery result:', result);
    
    if (!result.success) {
      console.error('[AvatarEditDrawer] Gallery failed:', result.error);
      if (result.error && result.error !== 'User canceled') {
        alert(`Gallery error: ${result.error}`);
      }
      return;
    }

    if (result.base64 && onChooseFromLibrary) {
      try {
        const file = await base64ToFile(result.base64, result.fileName || 'gallery-photo.jpg');
        console.log('[AvatarEditDrawer] Gallery file created:', file.name, file.size);
        onChooseFromLibrary(file);
      } catch (err) {
        console.error('[AvatarEditDrawer] Error converting gallery base64:', err);
        alert('Failed to process gallery photo');
      }
    }
  };

  // Register native message listeners
  useNativeMessages(handleNativeCameraResult, handleNativeGalleryResult);

  const handleTakePhoto = () => {
    // Trigger native camera if in WebView
    if (globalThis.window !== undefined && (globalThis.window as any).ReactNativeWebView) {
      triggerNativeCamera();
      onClose();
    } else {
      // Fallback to web file input
      cameraInputRef.current?.click();
    }
  };

  const handleChooseFromLibrary = () => {
    // Trigger native gallery if in WebView
    if (globalThis.window !== undefined && (globalThis.window as any).ReactNativeWebView) {
      triggerNativeGallery();
      onClose();
    } else {
      // Fallback to web file input
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onChooseFromLibrary) {
      onChooseFromLibrary(file);
    }
    onClose();
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onTakePhoto) {
      onTakePhoto(file);
    }
    onClose();
  };

  const handleDeletePhoto = () => {
    if (onDeletePhoto) {
      onDeletePhoto();
    }
    onClose();
  };

  return (
    <>
      <SFooterDrawer
        isOpen={isOpen}
        onClose={onClose}
        title="Edit Profile Photo"
        height="auto"
      >
        <div className="avatar-edit-drawer-content">
          {/* Take Photo Option */}
          <button className="avatar-edit-option" onClick={handleTakePhoto}>
            <span className="avatar-edit-option-label">Take Photo</span>
            <Camera size={24} color="#000000" />
          </button>

          {/* Choose from Library Option */}
          <button className="avatar-edit-option" onClick={handleChooseFromLibrary}>
            <span className="avatar-edit-option-label">Choose from Library</span>
            <Image size={24} color="#000000" />
          </button>

          {/* Delete Photo Option */}
          <button className="avatar-edit-option avatar-edit-option--delete" onClick={handleDeletePhoto}>
            <span className="avatar-edit-option-label avatar-edit-option-label--delete">Delete Photo</span>
            <Trash size={24} color="#EF4444" />
          </button>
        </div>
      </SFooterDrawer>

      {/* Hidden file input for library selection */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Hidden file input for camera capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />
    </>
  );
};

export default AvatarEditDrawer;
